import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
interface RefundHold {
  account: string;
  stopFlag: string | null;
  reason: string;
  provider: string;
  reference: string | null;
  createdAt: string;
}
export function RefundHoldsTab() {
  const client = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading, error, refetch, isFetching } = useQuery<{ items: RefundHold[]; warning: string | null }>({
    queryKey: ["refund-holds"],
    queryFn: async () => {
      const response = await fetch(`${BASE}/api/stripe-refunds/on-hold`, { credentials: "include" });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "Could not load refund holds");
      return body;
    },
    refetchInterval: 60_000,
  });
  const release = useMutation({
    mutationFn: async (account: string) => {
      const response = await fetch(`${BASE}/api/stripe-refunds/on-hold/${encodeURIComponent(account)}/release`, {
        method: "POST", credentials: "include", headers: { "X-Requested-With": "fetch" },
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "Could not release hold");
    },
    onSuccess: () => {
      for (const queryKey of [["refund-holds"], ["cn-queue"], ["backorder-search"]]) {
        void client.invalidateQueries({ queryKey });
      }
      toast({ title: "Hold released", description: "Stop flag verified as 0. No refund has been issued by this action." });
    },
    onError: (error: Error) => toast({ title: "Refund remains on hold", description: error.message, variant: "destructive" }),
  });
  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-semibold text-slate-900">On hold — human review</h2>
          <p className="text-sm text-slate-600">Stripe and Adflex refunds are blocked for these customers. A hold can only be released after Merlin’s customer stop flag is verified as 0.</p>
          <p className="mt-1 text-xs text-slate-500">Releasing a customer hold does not issue a refund or change Merlin. Other refund checks still apply.</p>
        </div>
        <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>Refresh</Button>
      </div>
      {isLoading && <p role="status">Checking refund holds…</p>}
      {error && <p role="alert" className="text-red-700">{error.message}</p>}
      {data?.warning && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-amber-800">{data.warning}</p>}
      {data && data.items.length === 0 && <p className="rounded-lg border p-6 text-sm text-slate-500">No customers have refunds on hold.</p>}
      {!!data?.items.length && (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              {["Account", "Flag when held", "Refund / order", "Reason", "Action"].map((label) => <th key={label} className="px-4 py-3">{label}</th>)}
            </tr></thead>
            <tbody>{data.items.map((hold) => (
              <tr key={hold.account} className="border-t">
                <td className="px-4 py-3 font-mono font-medium">{hold.account}</td>
                <td className="px-4 py-3">{hold.stopFlag ?? "Unverified"}</td>
                <td className="px-4 py-3">{hold.reference || "—"}<span className="block text-xs text-slate-500">{hold.provider === "adflex" ? "Adflex" : "Stripe"} · customer-wide hold</span></td>
                <td className="px-4 py-3 text-amber-800">{hold.reason}</td>
                <td className="px-4 py-3"><Button size="sm" variant="outline" disabled={release.isPending} onClick={() => release.mutate(hold.account)}>Re-check &amp; release</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
